import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Bootstrap() {
  const navigate = useNavigate();
  const { user, loginWithToken } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasAdmin, setHasAdmin] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", confirm: "", nom: "", prenom: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/auth/has-admin")
      .then(({ data }) => setHasAdmin(Boolean(data?.has_admin)))
      .catch(() => setHasAdmin(false))
      .finally(() => setChecking(false));
  }, []);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  if (checking) {
    return (
      <AuthShell title="Chargement..." subtitle="Vérification du système">
        <div className="h-2 bg-[#E5E7EB] rounded animate-pulse" />
      </AuthShell>
    );
  }
  if (hasAdmin) return <Navigate to="/login" replace />;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/bootstrap", {
        email: form.email.trim(),
        password: form.password,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
      });
      loginWithToken(data.token, data.user);
      toast.success("Compte administrateur créé");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Créer le compte Admin Siège"
      subtitle="Première installation — ce compte pilote l'ensemble de la plateforme."
      footer={null}
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="bootstrap-form">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="prenom">Prénom</Label>
            <Input id="prenom" data-testid="bootstrap-prenom" value={form.prenom} onChange={onChange("prenom")} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nom">Nom</Label>
            <Input id="nom" data-testid="bootstrap-nom" value={form.nom} onChange={onChange("nom")} required />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email professionnel</Label>
          <Input id="email" data-testid="bootstrap-email" type="email" value={form.email} onChange={onChange("email")} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Mot de passe (8 caractères min.)</Label>
          <Input id="password" data-testid="bootstrap-password" type="password" value={form.password} onChange={onChange("password")} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirmer le mot de passe</Label>
          <Input id="confirm" data-testid="bootstrap-confirm" type="password" value={form.confirm} onChange={onChange("confirm")} required />
        </div>

        {error && (
          <div data-testid="bootstrap-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="bootstrap-submit"
          disabled={submitting}
          className="w-full h-11 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl shadow-[0_4px_14px_rgba(249,200,14,0.32)]"
        >
          {submitting ? "Création..." : "Créer mon compte Admin"}
        </Button>
      </form>
    </AuthShell>
  );
}
